import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import Button from "../Button";
import logoVerticalDark from "../../assets/logo-vertical-dark.png";
import RichTextEditor from "../RichTextEditor";

const ClubBuilder = ({ data, onChange, onSave, selectedStore }) => {
  const [pageData, setPageData] = useState(data);
  const [collapsedSections, setCollapsedSections] = useState({
    header: true,
    hero: true,
    call_to_action: true,
    how_it_works: true,
    benefits: true,
    campaign: true,
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [debouncedData, setDebouncedData] = useState(data);
  const [countdown, setCountdown] = useState(0);
  const [viewMode, setViewMode] = useState("desktop"); // 'desktop' or 'mobile'
  const [previewContainerStyle, setPreviewContainerStyle] = useState({});
  const [iframeStyle, setIframeStyle] = useState({});
  const iframeRef = useRef(null);

  useEffect(() => {
    setPageData(data);
  }, [data]);

  // Debounce the data changes to prevent too frequent iframe refreshes
  useEffect(() => {
    setCountdown(1); // Reset countdown to 1 second

    const timer = setTimeout(() => {
      setDebouncedData(pageData);
      setCountdown(0);
    }, 20); // Wait 1 second after last change

    // Countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(countdownInterval);
    };
  }, [pageData]);

  useEffect(() => {
    // Generate preview URL with the debounced data as hash
    if (selectedStore?.uuid) {
      if (!previewUrl) {
        // Set initial URL only once
        setPreviewUrl(
          `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}`,
        );
      } else if (iframeRef.current) {
        // Update hash without reloading
        const dataHash = encodeURIComponent(JSON.stringify(debouncedData));
        const newUrl = `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}#${dataHash}`;

        // This won't reload the iframe, just changes the hash
        iframeRef.current.src = newUrl;
      }
    }
  }, [debouncedData, selectedStore, previewUrl]);

  useEffect(() => {
    const getPreviewContainerStyle = () => {
      const sidebarWidth =
        document.getElementById("sidebar-container")?.clientWidth || 384;
      const padding = 32;
      const border = 16;
      const headerHeight = 80;
      const headerPadding = 32;

      const availableWidth =
        window.innerWidth - sidebarWidth - padding - border;
      const availableHeight =
        window.innerHeight - headerHeight - headerPadding - border;

      return {
        width: `${availableWidth}px`,
        height: `${availableHeight}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      };
    };

    const getIframeStyle = () => {
      const sidebarWidth =
        document.getElementById("sidebar-container")?.clientWidth || 384;
      const padding = 32;
      const border = 16;
      const headerHeight = 80;
      const headerPadding = 32;

      const availableWidth =
        window.innerWidth - sidebarWidth - padding - border;
      const availableHeight =
        window.innerHeight - headerHeight - headerPadding - border;

      const baseStyle = {
        overflow: "hidden",
        flexShrink: 0,
      };

      if (viewMode === "mobile") {
        const mobileWidth = 320;
        const mobileHeight = 668;
        const borderSize = 48; // Extra space for phone frame
        const scale = Math.min(
          availableWidth / (mobileWidth + borderSize),
          availableHeight / (mobileHeight + borderSize),
        );

        return {
          ...baseStyle,
          width: "320px",
          height: "668px",
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          border: "3px solid #1f2937",
          borderRadius: "48px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        };
      }

      const desktopWidth = 1366;
      const desktopHeight = 768;
      const borderSize = 2;
      const scale = Math.min(
        availableWidth / (desktopWidth + borderSize),
        availableHeight / (desktopHeight + borderSize),
      );

      return {
        ...baseStyle,
        width: "1366px",
        height: "768px",
        transform: `scale(${scale})`,
        transformOrigin: "center center",
        borderRadius: "12px",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
      };
    };

    const handleResize = () => {
      setPreviewContainerStyle(getPreviewContainerStyle());
      setIframeStyle(getIframeStyle());
    };

    // Debounced resize handler
    let resizeTimeout;
    const debouncedHandleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 500); // 150ms debounce delay
    };

    window.addEventListener("resize", debouncedHandleResize);

    handleResize();

    // Cleanup function to remove the event listener and clear timeout
    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
      clearTimeout(resizeTimeout);
    };
  }, [viewMode]); // Add viewMode as dependency since it affects the iframe style calculation

  const updateNestedField = (path, value) => {
    const newData = { ...pageData };
    const keys = path.split(".");
    let current = newData;

    // Navigate to the parent object
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }

    // Update the field
    current[keys[keys.length - 1]] = value;

    setPageData(newData);
    onChange?.(newData);
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const addArrayItem = (path, template) => {
    const newData = { ...pageData };
    const keys = path.split(".");
    let current = newData;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }

    const arrayKey = keys[keys.length - 1];
    current[arrayKey].push(template);

    setPageData(newData);
    onChange?.(newData);
  };

  const removeArrayItem = (path, index) => {
    const newData = { ...pageData };
    const keys = path.split(".");
    let current = newData;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }

    const arrayKey = keys[keys.length - 1];
    current[arrayKey].splice(index, 1);

    setPageData(newData);
    onChange?.(newData);
  };

  const updateArrayItem = (path, index, field, value) => {
    const newData = { ...pageData };
    const keys = path.split(".");
    let current = newData;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }

    const arrayKey = keys[keys.length - 1];
    current[arrayKey][index] = { ...current[arrayKey][index], [field]: value };

    setPageData(newData);
    onChange?.(newData);
  };

  const renderField = (key, value, path) => {
    if (typeof value === "string") {
      // Check if this is a color field
      if (key === "text_color" || key === "background_color") {
        return (
          <div key={key} className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
              {key.replace(/_/g, " ")}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={value}
                onChange={(e) => updateNestedField(path, e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={value}
                onChange={(e) => updateNestedField(path, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#000000"
              />
            </div>
          </div>
        );
      }

      let textAreaPaths = ["call_to_action.content", "benefits.content"];

      if (textAreaPaths.includes(path)) {
        return (
          <div key={key} className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
              {key.replace(/_/g, " ")}
            </label>
            <RichTextEditor
              content={value}
              fullMode={true}
              onChange={(html) => updateNestedField(path, html)}
            />
          </div>
        );
      }

      return (
        <div key={key} className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
            {key.replace(/_/g, " ")}
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => updateNestedField(path, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      );
    }

    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        return (
          <div key={key} className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 capitalize">
                {key.replace(/_/g, " ")}
              </label>
              <Button
                variant="secondary"
                size="small"
                icon={PlusIcon}
                onClick={() => {
                  const template = getArrayItemTemplate(key);
                  addArrayItem(path, template);
                }}
              >
                Add Item
              </Button>
            </div>
            <div className="space-y-3">
              {value.map((item, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-600">
                      Item {index + 1}
                    </span>
                    <Button
                      variant="remove"
                      size="small"
                      icon={TrashIcon}
                      onClick={() => removeArrayItem(path, index)}
                    ></Button>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(item).map(([itemKey, itemValue]) => (
                      <div key={itemKey}>
                        <label className="block text-sm font-medium text-gray-600 mb-1 capitalize">
                          {itemKey.replace(/_/g, " ")}
                        </label>
                        <input
                          type="text"
                          value={itemValue}
                          onChange={(e) =>
                            updateArrayItem(
                              path,
                              index,
                              itemKey,
                              e.target.value,
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      return (
        <div key={key} className="mb-4">
          <button
            onClick={() => toggleSection(path)}
            className="flex items-center w-full text-left p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {collapsedSections[path] ? (
              <ChevronRightIcon className="w-5 h-5 mr-2" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 mr-2" />
            )}
            <span className="font-medium text-gray-700 capitalize">
              {key.replace(/_/g, " ")}
            </span>
          </button>
          {!collapsedSections[path] && (
            <div className="mt-3 pl-4 border-l-2 border-gray-200">
              {Object.entries(value).map(([subKey, subValue]) => (
                <div key={subKey}>
                  {renderField(subKey, subValue, `${path}.${subKey}`)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const getArrayItemTemplate = (arrayKey) => {
    const templates = {
      navigation: { name: "New Item", href: "/new-item" },
      steps: {
        title: "New Step",
        description: "Step description",
        image_url: "image-url",
      },
      items: {
        title: "New Item",
        description: "Item description",
        image_url: "image-url",
      },
    };
    return templates[arrayKey] || {};
  };

  const sectionConfigs = [
    { key: "header", title: "Header & Navigation", icon: "🏠" },
    { key: "hero", title: "Hero Section", icon: "⭐" },
    { key: "call_to_action", title: "Call to Action", icon: "📢" },
    { key: "how_it_works", title: "How It Works", icon: "🔧" },
    { key: "benefits", title: "Benefits", icon: "🎁" },
    { key: "campaign", title: "Campaigns", icon: "🎯" },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className="min-w-96 bg-white border-r border-gray-200 flex flex-col"
        id="sidebar-container"
      >
        <div className="p-4 border-b border-gray-200">
          <img src={logoVerticalDark} alt="Loyaltify" className="h-10" />
          <p className="text-sm text-gray-600 mt-1">
            Customize your club portal
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {sectionConfigs.map((config) => (
              <div
                key={config.key}
                className="border border-gray-200 rounded-lg"
              >
                <button
                  onClick={() => toggleSection(config.key)}
                  className="w-full p-3 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <span className="text-lg mr-2">{config.icon}</span>
                    <span className="font-medium text-gray-700">
                      {config.title}
                    </span>
                  </div>
                  {collapsedSections[config.key] ? (
                    <ChevronRightIcon className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {!collapsedSections[config.key] && (
                  <div className="border-t border-gray-200 p-4">
                    {Object.entries(pageData[config.key] || {}).map(
                      ([key, value]) => (
                        <div key={key}>
                          {renderField(key, value, `${config.key}.${key}`)}
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200">
          <Button onClick={() => onSave?.(pageData)} className="w-full">
            Save & Publish
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">
              Page Preview
            </h1>
            <div className="flex items-center space-x-4">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("desktop")}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "desktop"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <ComputerDesktopIcon className="w-4 h-4 mr-1.5" />
                  Desktop
                </button>
                <button
                  onClick={() => setViewMode("mobile")}
                  className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "mobile"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <DevicePhoneMobileIcon className="w-4 h-4 mr-1.5" />
                  Mobile
                </button>
              </div>

              {/* <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">
                                    {countdown > 0 ? `Auto-updates in ${countdown} ms` : 'Auto-updates every second'}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => {
                                        const previewData = encodeURIComponent(JSON.stringify(pageData));
                                        setPreviewUrl(`${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}#${previewData}`);
                                        setCountdown(0); // Reset countdown when manually refreshed
                                    }}
                                >
                                    Refresh Now
                                </Button>
                            </div> */}
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4 overflow-hidden">
          {previewUrl ? (
            <div style={previewContainerStyle}>
              <div style={{ position: "relative", ...iframeStyle }}>
                {viewMode === "mobile" ? (
                  <>
                    {/* Dynamic Island */}
                    <div
                      style={{
                        position: "absolute",
                        top: "16px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "80px",
                        height: "26px",
                        background: "#000",
                        borderRadius: "24px",
                        zIndex: 20,
                      }}
                    ></div>

                    {/* Screen container */}
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "white",
                        borderRadius: "40px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {/* Status Bar */}
                      <div
                        style={{
                          position: "absolute",
                          top: "20px",
                          left: "32px",
                          right: "32px",
                          zIndex: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: "10px",
                          fontWeight: "700",
                          color: "#000",
                        }}
                      >
                        <span>
                          {new Date().toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </span>
                      </div>

                      {/* Iframe */}
                      <iframe
                        ref={iframeRef}
                        src={previewUrl}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          display: "block",
                          paddingTop: "48px",
                        }}
                        onLoad={(e) => {
                          try {
                            const iframeDoc =
                              e.target.contentDocument ||
                              e.target.contentWindow.document;
                            const style = iframeDoc.createElement("style");
                            style.textContent = `
                              * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                              *::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
                              html, body { scrollbar-width: none !important; -ms-overflow-style: none !important; overflow-x: hidden !important; }
                            `;
                            iframeDoc.head.appendChild(style);
                          } catch (e) {
                            // Cross-origin iframe, can't access
                          }
                        }}
                        title="Club Page Preview"
                      />
                    </div>

                    {/* Home Indicator */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: "12px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "96px",
                        height: "4px",
                        background: "#1f2937",
                        borderRadius: "100px",
                        zIndex: 20,
                      }}
                    ></div>
                  </>
                ) : (
                  <iframe
                    ref={iframeRef}
                    src={previewUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      display: "block",
                    }}
                    onLoad={(e) => {
                      try {
                        const iframeDoc =
                          e.target.contentDocument ||
                          e.target.contentWindow.document;
                        const style = iframeDoc.createElement("style");
                        style.textContent = `
                                                    * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                                                    *::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
                                                    html, body { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                                                `;
                        iframeDoc.head.appendChild(style);
                      } catch (e) {
                        // Cross-origin iframe, can't access
                      }
                    }}
                    title="Club Page Preview"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <EyeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Preview Available
                </h3>
                <p className="text-gray-600">
                  Select a store to see the preview
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubBuilder;
